/**
 * Thrown by `callServerAction` when a mutation returns 202 because it is gated behind a
 * sensitive-action approval rule. The mutation has NOT been applied — a SensitiveAction is
 * awaiting admin review. Before throwing, `callServerAction` notifies the registered handler
 * (see {@link registerPendingApprovalHandler}), which surfaces the standard "awaiting
 * approval" modal — so most callers can let this propagate and only catch it to add behavior
 * (e.g. closing a sheet or emitting a tracking event).
 */
export class PendingApprovalError extends Error {
  constructor(readonly sensitiveActionId: string) {
    super('This action requires approval');
    this.name = 'PendingApprovalError';
  }
}

type PendingApprovalHandler = (sensitiveActionId: string) => void;

let handler: PendingApprovalHandler | null = null;

/**
 * Registers the app-level reaction to a gated mutation. `callServerAction` lives below the
 * modal layer and cannot open modals itself, so the app registers a handler once at root
 * (opening the `PendingApproval` modal) and the fetch layer calls it via {@link notifyPendingApproval}.
 * Returns an unregister function for cleanup.
 */
export function registerPendingApprovalHandler(fn: PendingApprovalHandler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) {
      handler = null;
    }
  };
}

// Best-effort: a no-op until PendingApprovalHandler mounts and registers. Safe in practice —
// approval-gated mutations are client-only and run after the app root has mounted.
export function notifyPendingApproval(sensitiveActionId: string): void {
  handler?.(sensitiveActionId);
}
